import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useToast } from '../../components/ui';
import { GetFunctionsByEventId, GetGuestsByEventId } from '../../api/api';
import { isMarriageEventType } from '../../utils/helpers';

const rowFunctionIds = (row) => {
  if (row.FunctionIds) return String(row.FunctionIds).split(',').map(v => v.trim()).filter(Boolean);
  return row.FunctionId ? [String(row.FunctionId)] : [];
};

// Shared logic for the venue headcount: persons per function, parties deduped
// by mobile (a family invited by several members counts once), split by Side.
export function useHeadcount(event) {
  const { isAdmin } = useAuth();
  const nav = useNavigate();
  const openRow = (fn) => nav('/app/members?fn=' + fn.FunctionId); // drill in to that function's guests
  const toast = useToast();
  const isTwoSided = isMarriageEventType(event?.EventTypeName || event?.EventType);
  const [functions, setFunctions] = useState([]);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(() => {
    setLoading(true);
    Promise.all([
      GetFunctionsByEventId({ EventId: event.EventId }),
      GetGuestsByEventId({ EventId: event.EventId }),
    ]).then(([f, g]) => {
      setFunctions(Array.isArray(f) ? f : []);
      setRows(Array.isArray(g) ? g : []);
    }).catch(() => toast('Failed to load')).finally(() => setLoading(false));
  }, [event, toast]);

  useEffect(() => { if (isAdmin) load(); }, [isAdmin, load]);

  const perFunction = useMemo(() => functions.map(fn => {
    const fnRows = rows.filter(r => rowFunctionIds(r).includes(String(fn.FunctionId)));
    const seen = new Map();
    fnRows.forEach(r => {
      const key = r.MobileNumber || `g${r.GuestId}`;
      const size = Number(r.ExpectedCount || 1);
      const prev = seen.get(key);
      if (!prev || size > prev.size) seen.set(key, { size, side: r.Side || 'Common' });
    });
    const bySide = { Groom: 0, Bride: 0, Common: 0 };
    let persons = 0;
    seen.forEach(v => {
      persons += v.size;
      const side = v.side === 'Male' ? 'Groom' : v.side === 'Female' ? 'Bride' : v.side;
      bySide[side] = (bySide[side] || 0) + v.size;
    });
    return { fn, parties: seen.size, persons, bySide };
  }), [functions, rows]);

  const grand = useMemo(
    () => perFunction.reduce((a, r) => ({ parties: a.parties + r.parties, persons: a.persons + r.persons }), { parties: 0, persons: 0 }),
    [perFunction]
  );

  return { isAdmin, loading, perFunction, grand, isTwoSided, openRow };
}
